import { createContext, useContext, useState } from "react";
import Select, { GroupBase, MenuPlacement } from "react-select";
import Creatable from "react-select/creatable";

import { SelectComponents } from "react-select/dist/declarations/src/components";
import * as FloSelectComponents from "./common";

const charcoal = "rgba(33, 33, 33, 1)";
const friendlyEskimo = "rgba(247, 249, 253, 1)";
const dublinGray = "rgba(221, 221, 221, 1)";
const magenta = "#ba2563";
const navy = "#286da8";
const macbook = "rgba(171, 171, 171, 1)";
const error = "rgb(219, 95, 83)";
const COLOR_VARIANTS = {
  magenta,
  navy,
};

export interface FloSelectOption {
  label: string;
  value: string;
}

type OnSelectItem = (
  option: FloSelectOption | readonly FloSelectOption[] | null
) => void;
type IsItemSelected = (option: FloSelectOption) => boolean;

const FloSelectAriaActiveDescendantContext = createContext<
  [string, (newString: string) => void]
>(["", () => {}]);
export function useFloSelectAriaActiveDescendantContext() {
  return useContext(FloSelectAriaActiveDescendantContext);
}

// Purposefully not exposing the full React-Select props interface
// as we figure out more use cases and edge cases we can add in the necessary props.
// to do that import { Props } from 'react-select';
export interface FloSelectProps {
  options: FloSelectOption[];
  name?: string;
  id?: string;

  onChange?: OnSelectItem;
  isOptionDisabled?: (option: FloSelectOption) => boolean;
  onCreateNewItem?: (itemName: string) => void;
  getOptionLabel?: (option: FloSelectOption) => string;

  menuPortalTarget?: HTMLElement;

  variant?: "magenta" | "navy";
  isMulti?: boolean;
  placeholder?: string;
  isClearable?: boolean;
  value: FloSelectOption | readonly FloSelectOption[];

  menuPlacement?: MenuPlacement;

  isLoading?: boolean;

  isSearchable?: boolean;
  hasError?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;

  required?: boolean;
  disabled?: boolean;
  tooltip?: string;

  /**
   * Message to display when there are no options present
   */
  noOptionsMessage?: string;

  UNSAFE_Components?: Partial<
    SelectComponents<FloSelectOption, boolean, GroupBase<FloSelectOption>>
  >;
}

export const FloSelectBase = (props: FloSelectProps) => {
  const color = props.hasError
    ? error
    : COLOR_VARIANTS[props.variant ?? "magenta"];

  const C = props.onCreateNewItem ? Creatable : Select;

  const [ariaActiveDescendant, setAriaActiveDescendant] = useState("");

  return (
    <FloSelectAriaActiveDescendantContext.Provider
      value={[ariaActiveDescendant, setAriaActiveDescendant]}
    >
      <C
        {...props}
        menuPlacement={props.menuPlacement}
        isDisabled={props.disabled}
        value={props.value}
        isSearchable={props.isSearchable}
        closeMenuOnSelect={!props.isMulti}
        onChange={(option) => {
          if (props.onChange) {
            if (props.isMulti) {
              props.onChange(option as readonly FloSelectOption[]);
            } else {
              props.onChange(option as FloSelectOption);
            }
          }
        }}
        components={{
          Input:
            props.UNSAFE_Components?.Input ??
            (props.required
              ? FloSelectComponents.InputWithRequiredAndEnterKeyFix
              : FloSelectComponents.InputWithEnterKeyFix),
          Option: FloSelectComponents.AccessibleOption,
          ...props.UNSAFE_Components,
        }}
        isLoading={props.isLoading}
        noOptionsMessage={() => props.noOptionsMessage}
        getOptionLabel={props.getOptionLabel ?? ((option) => option.label)}
        isOptionDisabled={props.isOptionDisabled ?? (() => false)}
        onBlur={props.onBlur}
        onFocus={props.onFocus}
        name={props.name}
        isClearable={props.isClearable ?? true}
        placeholder={props.placeholder ?? "Select an option"}
        menuPortalTarget={props.menuPortalTarget}
        menuPosition={"fixed"}
        styles={{
          menuPortal: (base, state) => {
            if (!props.menuPortalTarget) return;
            const el = props.menuPortalTarget;
            return {
              ...base,
              left: 0,
              top: 0,
              transform: `translate(${el.offsetLeft}px, ${
                el.offsetTop + el.clientHeight
              }px)`,
            };
          },
          menu: (base) => ({
            ...base,
            // Higher than ::before on radio button
            zIndex: 20,
          }),
          container: (base) => ({
            ...base,
            width: "100%",
            maxWidth: "100%",
          }),
          placeholder: (base) => ({
            ...base,
            color: macbook,
            fontSize: "0.875rem",
          }),
          control: (provided, state) => {
            return {
              ...provided,
              "&:hover": {
                borderColor: dublinGray,
              },
              height: "100%",
              borderColor:
                state.isFocused || props.hasError ? color : dublinGray,
              boxShadow: "none",
              outline:
                state.isFocused || props.hasError
                  ? `1px solid ${color}`
                  : "none",
            };
          },
          singleValue: (base) => ({
            ...base,
            fontSize: "0.875rem",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }),
          multiValueLabel: (base) => ({
            ...base,
            color: charcoal,
            fontWeight: 500,
          }),
          multiValue: (base) => ({
            ...base,
            backgroundColor: friendlyEskimo,
          }),
          noOptionsMessage: (base) => ({
            ...base,
            color: macbook,
            fontSize: "0.875rem",
          }),
          option: (base, state) => {
            const common = {
              cursor: "pointer",
            };
            if (state.isDisabled) {
              return {
                ...common,
                ...base,
                fontSize: "0.875rem",
              };
            }
            return {
              ...base,
              ...common,
              "&:active": {
                backgroundColor: friendlyEskimo,
              },
              "&:hover": {
                borderColor: dublinGray,
              },
              fontSize: "0.875rem",
              backgroundColor: state.isFocused ? friendlyEskimo : "transparent",
              color: state.isFocused || state.isSelected ? navy : charcoal,
              fontWeight: 500,
            };
          },
        }}
        options={props.options}
        isMulti={props.isMulti}
        onCreateOption={(inputValue: string) =>
          props.onCreateNewItem(inputValue)
        }
      />
    </FloSelectAriaActiveDescendantContext.Provider>
  );
};
